import { useMemo } from "react";
import {
  FilterType,
  FilterValue,
  InputType,
  SelectFilter,
  StringFilterValue,
} from "@coworker/apprestructured/src/shared/filters/types/filter";
import { ApplyFilter } from "@coworker/apprestructured/src/shared/filters/types/apply";
import { Article, Combined } from "../types/article";
import { useCustomAllSelectOption } from "@coworker/apprestructured/src/shared/filters/utils/useAllSelectOption";
import { useTranslation } from "@coworker/locales";
import { ALERT_ID } from "./useAlertSelectArticleFilter";

export const EDS_DATES_ID = "edsDates";

function useEdsDateOptions(edsDates: string[]) {
  return useMemo(
    () =>
      edsDates
        .sort()
        .reverse()
        .map((edsDate) => {
          const title = edsDate.split("T")[0] ?? edsDate;
          return { value: edsDate, title, dependsOnFilterId: ALERT_ID };
        }),
    [edsDates]
  );
}

function useEdsDatesSelectFilter(
  filterValues: FilterValue[],
  edsDates: string[]
) {
  const { t } = useTranslation();
  const defaultValue = { filterId: EDS_DATES_ID, value: "" };
  const edsDateSelectOptions = useEdsDateOptions(edsDates);
  const alertsValue = filterValues.find(
    ({ filterId }) => filterId === ALERT_ID
  );

  // Check if alerts filter has any of eds selections and adjust eds date
  // options accordingly.
  const enabled =
    !!alertsValue &&
    ["eds", "pastEds"].includes((alertsValue as StringFilterValue).value);
  const options = !enabled
    ? []
    : alertsValue.value === "pastEds"
      ? edsDateSelectOptions.filter(
          ({ value }) => new Date(value).getTime() < new Date().getTime()
        )
      : edsDateSelectOptions.filter(
          ({ value }) => new Date(value).getTime() >= new Date().getTime()
        );

  return useCustomAllSelectOption(
    {
      inputType: InputType.Select,
      filterType: FilterType.Filter,
      id: EDS_DATES_ID,
      title: t("edsDatesString"),
      options,
      enabled,
      defaultValue,
    },
    t("allEdsDatesString")
  );
}

function useEdsDatesSelectArticleFilter(
  filterValues: FilterValue[],
  edsDates: string[]
) {
  const filter = useEdsDatesSelectFilter(filterValues, edsDates);

  const applyFilter: ApplyFilter<SelectFilter, Article> = {
    filter,
    filterCompare: (item: Combined<Article>, { value }: FilterValue) => {
      if (filter.enabled && value) {
        if (!item.eds) {
          return false;
        }
        return value === item.eds.validToDateTime;
      } else {
        return true;
      }
    },
  };

  return applyFilter;
}

export { useEdsDatesSelectArticleFilter };
